<!--
© Copyright 2015-2026 Personalized Privacy Assistant Project, Carnegie Mellon University - All Rights Reserved - US Patent 11,768,949, US Patent 10,956,586, US Patent 12,235,973, and additional USPTO and EPO patents pending.
This code is proprietary and confidential. For any questions about this code, including licensing, please contact Prof. Norman Sadeh (sadeh@cs.cmu.edu).
-->
<template>
  <div>
    <div class="container-fluid">
      <div class="col-lg-9">
        <form id="irr-request-approve-form" method="POST" :action="'/irr-request/' + irrRequest.id">
          <input type="hidden" name="_token" :value="csrf_token">
          <input type="hidden" name="_method" value="PUT"> <!-- HTML forms can't make PUT or PATCH requests - add a hidden _method field to spoof these HTTP verbs -->
          <input type="hidden" id="approve" name="approve" value="-1">
          <input type="hidden" id="rejection_reason" name="rejection_reason" :value="rejectionReason">
          <div class="card bg-light mb-7">
            <center><h2 class="card-header">Registry Request</h2></center>
            <div class="card-body">
              <div v-if="irrRequest.org_legacy" class="card mb-3" style="border: 2px solid #dc3545; border-radius: 8px;">
                <div class="card-header" style="background-color: transparent; border-bottom: none; padding: 1rem 1.25rem 0;">
                  <h5 style="color: #dc3545 !important; margin: 0; font-weight: 600;">Legacy Request</h5>
                </div>
                <div class="card-body" style="padding: 0.5rem 1.25rem 1.25rem;">
                  <p class="card-text" style="color: #333; line-height: 1.6;">
                    This is an older request (before November 2019), so some fields and information may not be present. You can find the original request information at the bottom of the page. The request cannot be approved from this screen and will need to be handled manually by an administrator.
                  </p>
                </div>
              </div>
              <div id="irrmap" style="height: 400px"></div>
              &nbsp;<br />
              <div class="card-text">
                <div class="lead">
                  <div class="table-responsive">
                    <table class="table irr-request-details-table mb-0">
                      <tbody>
                      <tr>
                        <th scope="row" class="irr-request-details-label">Registry Owner</th>
                        <td class="text-break">{{ irrRequest.irr_owner_name }} <br /> {{ org_legal_text }}</td>
                      </tr>
                      <tr>
                        <th scope="row" class="irr-request-details-label">Type of Organization</th>
                        <td class="text-break">{{ irrRequest.org_type }}</td>
                      </tr>
                      <tr>
                        <th scope="row" class="irr-request-details-label">Address</th>
                        <td class="p-0">
                          <table class="table table-sm table-borderless mb-0 w-100 irr-request-nested-table">
                            <tr><td class="text-secondary text-nowrap pr-2">Street:</td><td class="text-break">{{ irrRequest.irr_owner_address_street }}</td></tr>
                            <tr><td class="text-secondary text-nowrap pr-2">City:</td><td class="text-break">{{ irrRequest.irr_owner_address_city }}</td></tr>
                            <tr><td class="text-secondary text-nowrap pr-2">State:</td><td class="text-break">{{ irrRequest.irr_owner_address_state }}</td></tr>
                            <tr><td class="text-secondary text-nowrap pr-2">Zip:</td><td class="text-break">{{ irrRequest.irr_owner_address_zip }}</td></tr>
                            <tr><td class="text-secondary text-nowrap pr-2">Country:</td><td class="text-break">{{ irrRequest.irr_owner_address_country }}</td></tr>
                          </table>
                        </td>
                      </tr>
                      <tr>
                        <th scope="row" class="irr-request-details-label">Contact</th>
                        <td class="p-0">
                          <table class="table table-sm table-borderless mb-0 w-100 irr-request-nested-table">
                            <tr><td class="text-secondary text-nowrap pr-2">Name:</td><td class="text-break">{{ irrRequest.contact_name }}</td></tr>
                            <tr><td class="text-secondary text-nowrap pr-2">Email:</td><td class="text-break">{{ irrRequest.contact_email }}</td></tr>
                            <tr v-if="contact_title_or_role"><td class="text-secondary text-nowrap pr-2">Title/Role:</td><td class="text-break">{{ contact_title_or_role }}</td></tr>
                          </table>
                        </td>
                      </tr>
                      <tr>
                        <th scope="row" class="irr-request-details-label">Registry Name</th>
                        <td class="text-break">{{ irrRequest.proposed_irr_name }}</td>
                      </tr>
                      <tr>
                        <th scope="row" class="irr-request-details-label">Publisher Name</th>
                        <td class="text-break">{{ irrRequest.publisher_name }}</td>
                      </tr>
                      <tr>
                        <th scope="row" class="irr-request-details-label">Description</th>
                        <td class="text-break">{{ irrRequest.irr_description }}</td>
                      </tr>
                      <tr>
                        <th scope="row" class="irr-request-details-label">Country</th>
                        <td class="text-break">{{ irrRequest.country }}</td>
                      </tr>
                      <tr>
                        <th scope="row" class="irr-request-details-label">Request Date</th>
                        <td class="text-break">{{ formatDate(irrRequest.created_at) }}</td>
                      </tr>
                      <tr v-if="irrRequest.pending">
                        <th scope="row" class="irr-request-details-label">Status</th>
                        <td class="text-break">Pending</td>
                      </tr>
                      <tr v-if="!irrRequest.pending">
                        <th scope="row" class="irr-request-details-label">Status</th>
                        <td class="text-break">{{ irrRequest.approved ? 'Approved' : 'Declined' }}</td>
                      </tr>
                      <tr>
                        <th scope="row" class="irr-request-details-label">Requesting User</th>
                        <td class="text-break">{{userDisplayName}}</td>
                        <!-- <td class="text-break"><a :href="'/user/' + irrRequest.user_id + '/'">{{userDisplayName}}</a></td> -->
                      </tr>
                      <tr>
                        <th class="border-0">&nbsp;</th><td class="border-0">&nbsp;</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-if="permissions.currentPermissions.SYS_APPROVE_IRR">
                    <button v-if="irrRequest.pending && !irrRequest.org_legacy" v-on:click="handleButton('decline')" type="button" class="btn btn-danger">Decline Registry Request</button>
                    <button v-if="irrRequest.pending && !irrRequest.org_legacy" v-on:click="handleButton('approve')" type="button" class="btn btn-primary float-right">Approve Registry Request</button>
                  </div>
                  <div v-if="irrRequest.org_legacy" class="card text-white bg-info mb-3">
                    <div class="card-header">Legacy Info</div>
                    <div class="card-body">
                      <p class="card-text">
                        {{ irrRequest.org_legacy_info }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Rejection Reason Modal -->
    <div class="modal fade" id="rejectionReasonModal" tabindex="-1" role="dialog" aria-labelledby="rejectionReasonModalLabel" aria-hidden="true" style="z-index: 10001;">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="rejectionReasonModalLabel">Reason for Rejection</h5>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <p>Please provide a reason for rejecting this registry request. This reason will be sent to the requestor via email.</p>
            <textarea v-model="rejectionReason" class="form-control" rows="5" placeholder="Enter rejection reason here..." maxlength="1000"></textarea>
            <small class="form-text text-muted mt-2">{{ rejectionReason.length }}/1000 characters</small>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-danger" @click="submitRejection" :disabled="!rejectionReason.trim()">Decline Request</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Approval Verification Modal -->
    <div class="modal fade" id="approvalVerificationModal" tabindex="-1" role="dialog" aria-labelledby="approvalVerificationModalLabel" aria-hidden="true" style="z-index: 10001;">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="approvalVerificationModalLabel">Approve Registry Request</h5>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="checkbox">
              <label>
                <input v-model="approvalVerified" type="checkbox">
                I have verified to the best of my ability the details that are claimed in this registry request and believe they are accurate.
              </label>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-success" @click="submitApproval" :disabled="!approvalVerified">Approve Request</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
    export default {
    props: {
      route: String,
      csrf_token: String,
      permissions: Object,
      irrRequest: [Object, Array],
      userDisplayName: String,
    },
    data: function() {
      return {
        rejectionReason: '',
        approvalVerified: false,
      }
    },
    computed: {
      org_legal_text: function () {
        if(this.irrRequest.org_legal == 1) {
          return '(legally registered organization)';
        }
        return '(an individual or informal group)';
      },
      contact_title_or_role: function () {
        var title = this.irrRequest.contact_title;
        if (title != null && String(title).trim() !== '') {
          return String(title).trim();
        }
        var role = this.irrRequest.contact_role;
        if (role != null && String(role).trim() !== '') {
          return String(role).trim();
        }
        return '';
      },
    },
    mounted: function () {
      this.$nextTick(function () {  //don't run until the entire view has been rendered
        //init the map at the IRR's location, or default to US if coordinates are invalid
        var lat = (this.irrRequest && this.irrRequest.latitude) ? this.irrRequest.latitude : 39.8;
        var lng = (this.irrRequest && this.irrRequest.longitude) ? this.irrRequest.longitude : -98.6;
        var zoom = (this.irrRequest && this.irrRequest.latitude) ? 16 : 4;
        this.map = window.L.map('irrmap').setView([lat, lng], zoom);

        //add the content
        //L.tileLayer('/map/tile/{s}/{id}/{z}/{x}/{y}', {  //for use when proxying the tile requests

        /* DEPRECATED L.tileLayer('https://api.tiles.mapbox.com/v4/{id}/{z}/{x}/{y}.png?access_token={accessToken}', {
          attribution: 'Map data &copy; <a href="https://www.openstreetmap.org/" target="_blank">OpenStreetMap</a> contributors, <a href="https://creativecommons.org/licenses/by-sa/2.0/" target="_blank">CC-BY-SA</a>',
          maxZoom: 18,
          minZoom: 3,
          id: 'mapbox.streets',
          accessToken: 'pk.eyJ1IjoiamRvbm5lbGwiLCJhIjoiY2ptZHN1azhqMHR2MDNxbnF2eWloOTRoeSJ9.RlFLdSY8e_anTpJNUJhIkA'
        }).addTo(this.map); */

        L.tileLayer('https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}', {
          attribution: '© <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> <strong><a href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a></strong>',
          tileSize: 512,
          maxZoom: 18,
          minZoom: 3,
          zoomOffset: -1,
          id: 'jdonnell/ckg9vp1o403lx1as8i81og3ir',
          accessToken: 'pk.eyJ1IjoiamRvbm5lbGwiLCJhIjoiY2ptZHN1azhqMHR2MDNxbnF2eWloOTRoeSJ9.RlFLdSY8e_anTpJNUJhIkA'
        }).addTo(this.map);

        window.L.control.scale().addTo(this.map);

        //draw a circle for the IRR's area of coverage (only if valid coordinates)
        if(this.irrRequest && this.irrRequest.latitude && this.irrRequest.longitude) {
        var areaOfAuthority = L.circle([lat, lng], {
          color: 'green',
          fillColor: '#0f3',
          fillOpacity: 0.1,
          radius: this.irrRequest.radius
        }).addTo(this.map);
        }

      })
    },
    methods: {
      handleButton: function($button) {
        if($button == 'approve') {
          // show approval verification modal instead of immediately submitting
          this.approvalVerified = false;
          $('#approvalVerificationModal').modal('show');
        }
        else if($button == 'decline') {
          // show rejection reason modal instead of immediately submitting
          this.rejectionReason = '';
          $('#rejectionReasonModal').modal('show');
        }
      },
      submitApproval: function() {
        if (this.approvalVerified) {
          $('#approve').val('1');
          $('#approvalVerificationModal').modal('hide');
          $('#irr-request-approve-form').submit();
        }
      },
      submitRejection: function() {
        if (this.rejectionReason.trim()) {
          $('#approve').val('0');
          $('#rejection_reason').val(this.rejectionReason);
          $('#rejectionReasonModal').modal('hide');
          $('#irr-request-approve-form').submit();
        }
      },
      formatDate: function(ts) {
        var date = new Date(ts);
        var day = date.getDate();
        var month = date.getMonth() + 1;
        var year = date.getFullYear();

        day = '0' + day;
        month = '0' + month;
        day = (day.length == 2) ? day : day.slice(1);
        month = (month.length == 2) ? month : month.slice(1);

        return year + '-' + month + '-' + day;
      },
    },
    }
</script>

<style scoped>
/* Keep label column minimal width so value cells use remaining space (avoids mid-width clipping). */
.irr-request-details-table th.irr-request-details-label {
  width: 1%;
  vertical-align: top;
  white-space: nowrap;
  font-weight: 600;
}

@media (max-width: 575.98px) {
  .irr-request-details-table th.irr-request-details-label {
    white-space: normal;
  }
}

.irr-request-nested-table td:first-child {
  width: 1%;
  vertical-align: top;
}
</style>
